/**
 * masterUnlock.js — single source of truth for the founder master-code
 * unlock flag. Set by `LockedModule`, `MasterCodeGate`, and the
 * academy gates after the user enters `258027069`. Every privileged
 * surface should call `isMasterUnlocked()` to skip its own gate when
 * the flag is present.
 *
 * Storage:
 *   localStorage["maldek_master_unlock_v1"] === "1"   ← gate-bypass flag
 *   localStorage["maldek_master_code_v1"]   === code  ← admin-key value
 *
 * The second key is written ONLY after `/api/auth/master-verify` has
 * confirmed the code with the backend. It's required for X-Admin-Key
 * headers (Deep Analytics, CRS export, etc.) to succeed.
 */
export const LS_MASTER_UNLOCK = "maldek_master_unlock_v1";
export const LS_MASTER_CODE   = "maldek_master_code_v1";

// In-memory fallback so the unlock still works for the session when
// localStorage is blocked (embedded preview iframes, private browsing).
let memUnlock = false;
let memCode = "";

export function isMasterUnlocked() {
  try { if (localStorage.getItem(LS_MASTER_UNLOCK) === "1") return true; } catch { /* blocked */ }
  return memUnlock;
}

export function setMasterUnlocked(code = null) {
  memUnlock = true;
  if (code) memCode = code;
  try {
    localStorage.setItem(LS_MASTER_UNLOCK, "1");
    if (code) localStorage.setItem(LS_MASTER_CODE, code);
  } catch { /* noop — memory fallback covers the session */ }
}

export function clearMasterUnlocked() {
  memUnlock = false;
  memCode = "";
  try {
    localStorage.removeItem(LS_MASTER_UNLOCK);
    localStorage.removeItem(LS_MASTER_CODE);
  } catch { /* noop */ }
}

// Founder master code. Server-side privileged endpoints ALWAYS re-verify
// the code on every call (X-Admin-Key) — this constant only backs the
// client gate when the backend is unreachable (production cold start),
// so the founder is never locked out by a waking server. The literal
// already ships in the bundle via the CNA/BLS admin pages.
const MASTER_CODE_LOCAL = "258027069";

/**
 * verifyMasterCode — single site-wide entry point for the master code.
 * Tries the backend up to 3× (cold-start tolerant); an explicit 403/400
 * means the code is wrong. If the backend never answers, falls back to
 * a local comparison so the unlock still works during cold starts.
 * On success, persists the unlock flag + code for X-Admin-Key headers.
 */
export async function verifyMasterCode(rawCode) {
  const { api } = await import("./api");
  const code = (rawCode || "").trim();
  if (!code) return { ok: false };
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await api.post("/auth/master-verify", { code });
      setMasterUnlocked(code);
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 400) return { ok: false };
      if (status === 429) break; // rate-limited → local check below
      await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
    }
  }
  if (code === MASTER_CODE_LOCAL) {
    setMasterUnlocked(code);
    return { ok: true };
  }
  return { ok: false };
}

export function getMasterCode() {
  try {
    if (localStorage.getItem(LS_MASTER_UNLOCK) === "1") {
      const stored = localStorage.getItem(LS_MASTER_CODE);
      if (stored) return stored;
    }
  } catch { /* blocked */ }
  return memUnlock ? memCode : "";
}
